import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

export interface TestimonialProps {
  id?: string;
  quote: string;
  name: string;
  location: string;
  image: string;
  delay: number;
}

interface UseTestimonialsReturn {
  testimonials: TestimonialProps[];
  isLoading: boolean;
  error: string | null;
}

// Dữ liệu mẫu sẽ được sử dụng nếu có lỗi khi tải từ Supabase
const MOCK_TESTIMONIALS: TestimonialProps[] = [
  {
    quote:
      "GlowHer.Life has transformed my self-care and fashion approach! I feel more confident and stylish while saving money with their smart shopping tips.",
    name: "Emma R.",
    location: "New York",
    image:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=256&q=80",
    delay: 0.1,
  },
  {
    quote:
      "Smart shopping tips helped me save money while staying elegant! Their Beauty section has completely upgraded my skincare routine with affordable yet effective products.",
    name: "Olivia M.",
    location: "Los Angeles",
    image:
      "https://images.unsplash.com/photo-1554727242-741c14fa561c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=256&q=80",
    delay: 0.2,
  },
  {
    quote:
      "A community where women uplift each other with style and confidence. The work-life balance articles helped me restructure my daily routine and find more joy in both areas.",
    name: "Sophia T.",
    location: "Chicago",
    image:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=256&q=80",
    delay: 0.3,
  },
];

export const useTestimonials = (): UseTestimonialsReturn => {
  const [testimonials, setTestimonials] = useState<TestimonialProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTestimonials = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Lấy dữ liệu từ bảng testimonials trong Supabase
        const { data, error } = await supabase
          .from("testimonials")
          .select("*")
          .eq("active", true)
          .order("created_at", { ascending: false });

        if (error) throw error;

        if (data && data.length > 0) {
          // Giới hạn số lượng testimonials hiển thị để UI trông tốt hơn
          const displayCount = Math.min(data.length, 3);
          const selectedData = data.slice(0, displayCount);

          // Chuyển đổi dữ liệu từ Supabase sang định dạng TestimonialProps và thêm delay
          const testimonialsWithDelay = selectedData.map(
            (testimonial, index) => ({
              id: testimonial.id,
              quote: testimonial.quote,
              name: testimonial.name,
              location: testimonial.location,
              image: testimonial.image,
              delay: index * 0.1,
            })
          );

          setTestimonials(testimonialsWithDelay);
        } else {
          // Nếu không có dữ liệu, sử dụng dữ liệu mẫu
          console.warn(
            "Không có testimonials từ Supabase, sử dụng dữ liệu mẫu."
          );
          setTestimonials(MOCK_TESTIMONIALS);
        }
      } catch (error) {
        console.error("Lỗi khi tải dữ liệu testimonials:", error);
        setError("Không thể tải đánh giá. Vui lòng thử lại sau.");
        // Fallback to mock data
        setTestimonials(MOCK_TESTIMONIALS);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  return {
    testimonials,
    isLoading,
    error,
  };
};

export default useTestimonials;
